class MobileInlineSearch extends HTMLElement {
  constructor() {
    super();

    this.headerSection = document.querySelector('.shopify-section-header');
    this.searchModal = document.querySelector('search-modal');
    this.searchToggle = this.searchModal?.querySelector('summary');
    this.overlay = this.querySelector('[data-mobile-search-overlay]');
    this.input = this.querySelector('input[type="search"]');
    this.closeBtn = this.querySelector('[data-mobile-search-close]');
    this.predictiveSearch = this.querySelector('predictive-search');
    this.menuDrawer = document.querySelector('header-drawer');
    this.cartDrawer = document.querySelector('cart-drawer');
    this.facetDrawers = document.querySelectorAll('facet-drawer');
    this.mql = window.matchMedia('(max-width: 989px)');
    this.wasSticky = false;
    this.isProductPage = false;
    this.userRevealed = false;
    this.metricsFrame = null;
    this.viewportFrame = null;
    this.lastMetricsTop = null;
    this.lastMetricsHeight = null;
    this.resizeObserver = null;

    this.drawerBodyClasses = [
      'menu-mobile--open',
      'menu-mobile--opening',
      'mini-cart--open',
      'mini-cart--opening',
      'facet-drawer--open',
      'facet-drawer--opening'
    ];

    this.onCloseClick = this.onCloseClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.updateStickyState = this.updateStickyState.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.onHeaderSearchClick = this.onHeaderSearchClick.bind(this);
    this.onPredictiveOpen = this.onPredictiveOpen.bind(this);
    this.onViewportResize = this.onViewportResize.bind(this);
    this.onDrawerClose = this.onDrawerClose.bind(this);
    this.syncSearchMetrics = this.syncSearchMetrics.bind(this);
  }

  connectedCallback() {
    this.isProductPage = this.dataset.clickToShow === 'true';

    this.closeBtn?.addEventListener('click', this.onCloseClick, true);
    this.overlay?.addEventListener('click', this.onOverlayClick);
    this.input?.addEventListener('input', this.onInput);
    this.searchToggle?.addEventListener('click', this.onHeaderSearchClick, true);

    if (this.headerSection) {
      this.stickyObserver = new MutationObserver(this.updateStickyState);
      this.stickyObserver.observe(this.headerSection, { attributes: true, attributeFilter: ['class'] });
    }

    this.bodyObserver = new MutationObserver((mutations) => {
      const drawerStateChanged = mutations.some((mutation) => {
        if (mutation.attributeName !== 'class') return false;

        const previousClasses = mutation.oldValue || '';
        const currentClasses = document.body.className;
        return this.drawerBodyClasses.some(
          (drawerClass) =>
            previousClasses.includes(drawerClass) !== currentClasses.includes(drawerClass)
        );
      });

      if (!drawerStateChanged) return;

      const drawerOpened = mutations.some((mutation) => {
        if (mutation.attributeName !== 'class') return false;

        const previousClasses = mutation.oldValue || '';
        return this.isDrawerOpen() && !this.hasDrawerBodyClass(previousClasses);
      });

      if (drawerOpened) {
        this.resetSearchSession(true);
      }

      this.syncBarVisibility();
    });
    this.bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true
    });

    if (this.predictiveSearch) {
      this.predictiveObserver = new MutationObserver(this.onPredictiveOpen);
      this.predictiveObserver.observe(this.predictiveSearch, {
        attributes: true,
        attributeFilter: ['open', 'results', 'loading']
      });
    }

    this.menuDrawer?.addEventListener('close', this.onDrawerClose);
    this.cartDrawer?.addEventListener('close', this.onDrawerClose);
    this.facetDrawers.forEach((drawer) => drawer.addEventListener('close', this.onDrawerClose));
    this.mql.addEventListener('change', this.onBreakpointChange);
    window.addEventListener('resize', this.onViewportResize, { passive: true });

    this.wasSticky = this.isHeaderSticky();
    this.syncMobileViewport();
    requestAnimationFrame(() => this.scheduleSearchMetrics());
  }

  disconnectedCallback() {
    this.closeBtn?.removeEventListener('click', this.onCloseClick, true);
    this.overlay?.removeEventListener('click', this.onOverlayClick);
    this.input?.removeEventListener('input', this.onInput);
    this.searchToggle?.removeEventListener('click', this.onHeaderSearchClick, true);
    this.stickyObserver?.disconnect();
    this.bodyObserver?.disconnect();
    this.predictiveObserver?.disconnect();
    this.menuDrawer?.removeEventListener('close', this.onDrawerClose);
    this.cartDrawer?.removeEventListener('close', this.onDrawerClose);
    this.facetDrawers.forEach((drawer) => drawer.removeEventListener('close', this.onDrawerClose));
    this.mql.removeEventListener('change', this.onBreakpointChange);
    window.removeEventListener('resize', this.onViewportResize);
    this.setMetricsObserver(false);
    this.cancelScheduledWork();
    this.clearSearchMetrics();
    this.deactivate();
  }

  cancelScheduledWork() {
    if (this.metricsFrame) {
      cancelAnimationFrame(this.metricsFrame);
      this.metricsFrame = null;
    }

    if (this.viewportFrame) {
      cancelAnimationFrame(this.viewportFrame);
      this.viewportFrame = null;
    }
  }

  onViewportResize() {
    if (this.viewportFrame) return;

    this.viewportFrame = requestAnimationFrame(() => {
      this.viewportFrame = null;
      this.syncMobileViewport();
    });
  }

  setMetricsObserver(enabled) {
    if (enabled) {
      if (this.resizeObserver) return;

      this.resizeObserver = new ResizeObserver(() => this.scheduleSearchMetrics());
      this.resizeObserver.observe(this);
      return;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  scheduleSearchMetrics() {
    if (this.metricsFrame) return;

    this.metricsFrame = requestAnimationFrame(() => {
      this.metricsFrame = null;
      this.syncSearchMetrics();
    });
  }

  clearSearchMetrics() {
    if (this.lastMetricsTop === null && this.lastMetricsHeight === null) return;

    this.lastMetricsTop = null;
    this.lastMetricsHeight = null;
    document.documentElement.style.removeProperty('--fc-mobile-search-height');
    document.documentElement.style.removeProperty('--fc-mobile-search-top');
  }

  syncSearchMetrics() {
    if (!this.mql.matches || this.isBarHidden()) {
      this.clearSearchMetrics();
      this.setMetricsObserver(false);
      return;
    }

    const anchor = this.closest('sticky-header, header-wrapper');
    const header = anchor?.querySelector('.header');
    if (!anchor || !header) return;

    const nextTop = `${header.offsetHeight}px`;
    const nextHeight = `${this.offsetHeight}px`;

    if (this.lastMetricsTop === nextTop && this.lastMetricsHeight === nextHeight) {
      return;
    }

    this.lastMetricsTop = nextTop;
    this.lastMetricsHeight = nextHeight;
    document.documentElement.style.setProperty('--fc-mobile-search-top', nextTop);
    document.documentElement.style.setProperty('--fc-mobile-search-height', nextHeight);

    if (typeof setHeaderBottomPosition === 'function') {
      setHeaderBottomPosition();
    }
  }

  hasDrawerBodyClass(className) {
    return this.drawerBodyClasses.some((drawerClass) => className.includes(drawerClass));
  }

  isHeaderSticky() {
    return Boolean(this.headerSection?.classList.contains('shopify-section-header-sticky'));
  }

  isDrawerOpen() {
    return this.drawerBodyClasses.some((drawerClass) => document.body.classList.contains(drawerClass));
  }

  isBarHidden() {
    return this.classList.contains('fc-mobile-search--hidden');
  }

  shouldShowBar() {
    if (!this.mql.matches || this.isDrawerOpen()) return false;
    if (this.isHeaderSticky()) return false;
    if (this.isProductPage) return this.userRevealed;
    return true;
  }

  syncBarVisibility() {
    if (this.shouldShowBar()) {
      this.showBar();
    } else {
      this.hideBar();
    }
  }

  showBar() {
    if (!this.mql.matches) return;

    const wasHidden = this.isBarHidden();

    if (wasHidden) {
      this.classList.remove('fc-mobile-search--hidden');
    }

    this.setMetricsObserver(true);

    if (wasHidden || this.lastMetricsTop === null) {
      this.scheduleSearchMetrics();
    }
  }

  hideBar() {
    if (this.isBarHidden()) return;

    this.classList.add('fc-mobile-search--hidden');
    this.setMetricsObserver(false);
    this.deactivate();
    this.clearSearchMetrics();
  }

  hasPredictiveResults() {
    if (!this.predictiveSearch) return false;

    const hasResultsAttr = this.predictiveSearch.hasAttribute('results');
    const hasOpenAttr = this.predictiveSearch.hasAttribute('open');
    const resultsNode = this.predictiveSearch.querySelector('[data-predictive-search]');
    const hasMarkup = Boolean(resultsNode?.innerHTML.trim());

    return hasOpenAttr && hasResultsAttr && hasMarkup;
  }

  shouldShowOverlay() {
    return Boolean(this.input?.value.trim().length) && this.hasPredictiveResults();
  }

  resetSearchSession(silent = false) {
    const hadValue = Boolean(this.input?.value);

    if (this.input && hadValue) {
      this.input.value = '';

      if (!silent) {
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    if (this.predictiveSearch?.close) {
      this.predictiveSearch.close(true);
      const results = this.predictiveSearch.querySelector('[data-predictive-search]');
      if (results) results.innerHTML = '';
    }

    this.deactivate();
  }

  closeSearchModal() {
    const details = this.searchModal?.querySelector('details');
    if (details?.hasAttribute('open')) {
      details.removeAttribute('open');
      this.searchModal?.close?.();
    }

    if (document.body.classList.contains('search-modal--open')) {
      document.body.classList.remove('search-modal--open');
    }
  }

  syncMobileViewport() {
    if (!this.mql.matches) {
      this.closeSearchModal();
      this.resetSearchSession(true);
      this.userRevealed = false;
      this.syncBarVisibility();
      return;
    }

    this.closeSearchModal();

    if (this.isDrawerOpen()) {
      this.resetSearchSession(true);
    }

    this.updateStickyState();
    this.scheduleSearchMetrics();
  }

  onDrawerClose() {
    if (!this.mql.matches) return;
    this.syncBarVisibility();
  }

  onBreakpointChange(event) {
    this.cancelScheduledWork();

    if (!event.matches) {
      this.resetSearchSession(true);
      this.userRevealed = false;
      this.closeSearchModal();
      this.wasSticky = false;
      this.syncBarVisibility();
      return;
    }

    this.userRevealed = false;
    this.wasSticky = this.isHeaderSticky();
    this.syncMobileViewport();
  }

  updateStickyState() {
    if (!this.mql.matches) return;

    const isSticky = this.isHeaderSticky();

    if (!isSticky) {
      if (this.wasSticky) {
        this.wasSticky = false;
      }
    } else if (!this.wasSticky) {
      this.wasSticky = true;
      this.resetSearchSession(true);

      if (this.isProductPage) {
        this.userRevealed = false;
      }
    }

    this.syncBarVisibility();
  }

  onHeaderSearchClick(event) {
    if (!this.mql.matches || this.isDrawerOpen()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.closeSearchModal();

    if (this.isBarHidden()) {
      if (this.isProductPage) {
        this.userRevealed = true;
      }

      this.showBar();
      requestAnimationFrame(() => this.input?.focus());
    } else {
      if (this.isProductPage) {
        this.userRevealed = false;
      }

      this.resetSearchSession(true);
      this.hideBar();
    }
  }

  onPredictiveOpen() {
    if (this.shouldShowOverlay()) {
      this.activate();
    } else {
      this.deactivate();
    }
  }

  onInput() {
    if (!this.input?.value.trim().length) {
      this.deactivate();

      if (this.predictiveSearch?.close) {
        this.predictiveSearch.close(true);
      }
      return;
    }

    if (this.shouldShowOverlay()) {
      this.activate();
    } else {
      this.deactivate();
    }
  }

  onCloseClick(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.isProductPage) {
      this.userRevealed = false;
    }

    this.resetSearchSession(true);
    this.input?.blur();
    this.hideBar();
  }

  onOverlayClick() {
    this.onCloseClick(new Event('click'));
  }

  setOverlayTop() {
    const bottom = this.getBoundingClientRect().bottom;
    document.documentElement.style.setProperty('--fc-mobile-search-overlay-top', `${bottom}px`);
  }

  activate() {
    if (!this.mql.matches || this.isBarHidden() || this.isDrawerOpen() || !this.shouldShowOverlay()) {
      return;
    }

    if (this.classList.contains('fc-mobile-search--active')) return;

    if (typeof setScrollbarWidth === 'function') setScrollbarWidth();

    this.setOverlayTop();
    this.classList.add('fc-mobile-search--active');

    if (!document.body.classList.contains('fc-mobile-search--open')) {
      document.body.classList.add('fc-mobile-search--open');
    }

    this.overlay?.setAttribute('aria-hidden', 'false');
    this.scheduleSearchMetrics();

    if (!this.overlayHandler) {
      this.overlayHandler = () => {
        if (this.classList.contains('fc-mobile-search--active')) this.setOverlayTop();
      };
      window.addEventListener('resize', this.overlayHandler, { passive: true });
      window.addEventListener('scroll', this.overlayHandler, { passive: true });
    }
  }

  deactivate() {
    if (
      !this.classList.contains('fc-mobile-search--active') &&
      !document.body.classList.contains('fc-mobile-search--open')
    ) {
      return;
    }

    this.classList.remove('fc-mobile-search--active');

    if (document.body.classList.contains('fc-mobile-search--open')) {
      document.body.classList.remove('fc-mobile-search--open');
    }

    this.overlay?.setAttribute('aria-hidden', 'true');

    if (this.overlayHandler) {
      window.removeEventListener('resize', this.overlayHandler);
      window.removeEventListener('scroll', this.overlayHandler);
      this.overlayHandler = null;
    }
  }
}

customElements.define('mobile-inline-search', MobileInlineSearch);
